import React, { useMemo } from 'react';
import { HEAD_DOCTOR_COPY } from '../headDoctorCopy';
import { HeadDoctorStatCard } from '../ui';
import { useClinicalReports } from '../../clinical-reports/ClinicalReportsContext';
import ReportSectionHead from './ReportSectionHead';
import SynthesisActivityTable from './SynthesisActivityTable';
import {
  ClinicalBarChart,
  ClinicalDoughnutChart,
  ReportChartCard,
  ReportsChartCol,
  ReportsChartGrid
} from './charts/ClinicalCharts';
import {
  doughnutChartData,
  histogramChartData,
  topNWithOthers,
  truncateLabel
} from './charts/chartDataHelpers';
import { buildSynthesisRows, countDaysInclusive } from './synthesisTableModel';

const COPY = HEAD_DOCTOR_COPY.REPORTS;

export default function HeadDoctorReportsSynthesePage() {
  const { data, summary, loading, periodLabel, reportPaths } = useClinicalReports();

  const days = useMemo(
    () => countDaysInclusive(data?.range?.from, data?.range?.to),
    [data?.range?.from, data?.range?.to]
  );

  const synthesisGroups = useMemo(
    () =>
      buildSynthesisRows(
        summary,
        data?.consultations?.by_service_line,
        days,
        reportPaths || undefined
      ),
    [summary, data?.consultations?.by_service_line, days, reportPaths]
  );

  const pathologyPie = useMemo(() => {
    const rows = data?.pathologies?.rows || [];
    const pie = topNWithOthers(rows, 'consultation_count', 'disease_name', 5, COPY.CHART_OTHERS);
    return doughnutChartData(pie.labels, pie.values, pie.colors);
  }, [data]);

  const medsHistogram = useMemo(() => {
    const rows = [...(data?.medications?.rows || [])]
      .sort((a, b) => Number(b.total_quantity || 0) - Number(a.total_quantity || 0))
      .slice(0, 10);
    const chart = histogramChartData(
      rows.map((r) => truncateLabel(r.medication_name, 16)),
      rows.map((r) => Number(r.total_quantity || 0)),
      COPY.COL_QUANTITY
    );
    chart.datasets[0].prescriptionCounts = rows.map((r) => Number(r.prescription_count || 0));
    chart.datasets[0].medicationNames = rows.map((r) => r.medication_name);
    return chart;
  }, [data]);

  const medsChartOptions = useMemo(
    () => ({
      onHover(event, elements) {
        const canvas = event?.native?.target;
        if (canvas?.style) {
          canvas.style.cursor = elements?.length ? 'pointer' : 'default';
        }
      },
      plugins: {
        tooltip: {
          callbacks: {
            title(items) {
              const idx = items?.[0]?.dataIndex;
              const names = items?.[0]?.dataset?.medicationNames;
              return names?.[idx] || items?.[0]?.label || '';
            },
            label(ctx) {
              const qty = Number(ctx.parsed?.y || 0);
              return `${COPY.COL_QUANTITY} : ${qty.toLocaleString('fr-FR')}`;
            },
            afterLabel(ctx) {
              const rx = Number(ctx.dataset?.prescriptionCounts?.[ctx.dataIndex] || 0);
              return `${COPY.CHART_PRESCRIPTIONS} : ${rx.toLocaleString('fr-FR')}`;
            }
          }
        }
      }
    }),
    []
  );

  const pathologyRate = summary.consultations
    ? Math.round((summary.consultations_with_pathology / summary.consultations) * 100)
    : 0;

  const hasMeds = !loading && (data?.medications?.rows || []).length > 0;
  const hasPatho = !loading && (data?.pathologies?.rows || []).length > 0;

  return (
    <>
      <ReportSectionHead
        badge={COPY.SUB_SYNTHESE}
        title={COPY.PAGE_SYNTHESE_TITLE}
        description={COPY.PAGE_SYNTHESE_DESC}
      />

      <div className="hd-reports-kpis row g-3 mb-3">
        <div className="col-6 col-lg-3">
          <HeadDoctorStatCard
            title={COPY.KPI_CONSULTATIONS}
            value={summary.consultations}
            icon="stethoscope"
            tone="indigo"
            loading={loading}
            subtitle={COPY.KPI_CONSULTATIONS_HINT}
          />
        </div>
        <div className="col-6 col-lg-3">
          <HeadDoctorStatCard
            title={COPY.KPI_PATHOLOGIES}
            value={summary.unique_pathologies}
            icon="alertCircle"
            tone="amber"
            loading={loading}
            subtitle={`${pathologyRate} % ${COPY.KPI_PATHOLOGY_RATE_HINT}`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <HeadDoctorStatCard
            title={COPY.KPI_PRESCRIPTIONS}
            value={summary.prescriptions}
            icon="pills"
            tone="teal"
            loading={loading}
            subtitle={`${summary.prescription_fulfilled || 0} ${COPY.KPI_PRESCRIPTIONS_HINT}`}
          />
        </div>
        <div className="col-6 col-lg-3">
          <HeadDoctorStatCard
            title={COPY.KPI_MEDICATIONS}
            value={summary.unique_medications}
            icon="chartBar"
            tone="slate"
            loading={loading}
            subtitle={`${summary.total_units_prescribed || 0} ${COPY.KPI_MEDICATIONS_HINT}`}
          />
        </div>
      </div>

      <SynthesisActivityTable
        className="mb-4"
        groups={synthesisGroups}
        days={days}
        periodLabel={periodLabel || ''}
        loading={loading}
        emptyLabel={COPY.LOADING}
      />

      {loading ? (
        <div className="hd-reports-panel mb-4">
          <div className="hd-reports-loading">
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
            <span>{COPY.LOADING_CHARTS}</span>
          </div>
        </div>
      ) : hasPatho || hasMeds ? (
        <ReportsChartGrid>
          {hasPatho ? (
            <ReportsChartCol lg={hasMeds ? 5 : 12}>
              <ReportChartCard
                title={COPY.CHART_PATHO_SHARE_TITLE}
                subtitle={COPY.CHART_PATHO_SHARE_DESC}
                height={320}
              >
                <ClinicalDoughnutChart data={pathologyPie} />
              </ReportChartCard>
            </ReportsChartCol>
          ) : null}
          {hasMeds ? (
            <ReportsChartCol lg={hasPatho ? 7 : 12}>
              <ReportChartCard
                title={COPY.CHART_MED_TOP_TITLE}
                subtitle={COPY.CHART_MED_HIST_DESC}
                height={320}
              >
                <ClinicalBarChart
                  data={medsHistogram}
                  yLabel={COPY.COL_QUANTITY}
                  xLabel={COPY.CHART_AXIS_MEDICATION}
                  showLegend={false}
                  options={medsChartOptions}
                />
              </ReportChartCard>
            </ReportsChartCol>
          ) : null}
        </ReportsChartGrid>
      ) : null}
    </>
  );
}
